/**
 * Who is making the page wider than the screen.
 *
 * "Su telefoni diversi l'app non si adatta allo schermo": on two phones that
 * are not Silvia's, the right-hand side of every row is cut off. From a
 * photograph I can see THAT it happens and not WHY, and I have already spent
 * two fixes this week on causes I inferred from pictures and got wrong.
 *
 * So the app measures itself. It compares the width of the document with the
 * width of the window, and if the first is bigger it walks the tree for the
 * elements that stick out, describing each one well enough to find it in the
 * source. It runs only when there IS an overflow, which on a correct layout is
 * never, and what it produces is a line Silvia can photograph and send.
 *
 * Nothing here fixes anything. It ends an argument.
 */

/** A short, human-findable description of an element. */
function describe(el) {
  const cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/).filter(Boolean) : [];
  const bits = [el.tagName.toLowerCase()];
  if (el.id) bits.push('#' + el.id);
  if (cls.length) bits.push('.' + cls.slice(0, 2).join('.'));
  const text = (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 18);
  if (text) bits.push('“' + text + '”');
  return bits.join(' ');
}

/**
 * Returns null when the page fits, or a report when it does not.
 * { page, screen, over, worst: [{ what, right, width }] }
 */
export function findOverflow() {
  try {
    const doc = document.documentElement;
    const screen = doc.clientWidth;
    const page = Math.max(doc.scrollWidth, document.body ? document.body.scrollWidth : 0);
    const over = page - screen;
    if (over <= 1) return null;                 // sub-pixel rounding is not an overflow

    const worst = [];
    const all = document.querySelectorAll('body *');
    for (const el of all) {
      // A fixed element pinned to the right edge is not the culprit; it is
      // measured against the viewport and always sits inside it.
      const st = window.getComputedStyle(el);
      if (st.position === 'fixed') continue;
      const r = el.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      if (r.right <= screen + 1) continue;
      worst.push({ what: describe(el), right: Math.round(r.right), width: Math.round(r.width) });
    }
    // The outermost offenders first: a child sticking out usually means its
    // parent is the one that cannot hold it.
    worst.sort((a, b) => b.width - a.width);
    return { page, screen, over, worst: worst.slice(0, 4) };
  } catch (_) {
    return null;
  }
}

/** The same thing as one line, ready to be read out or photographed. */
export function overflowLine() {
  const r = findOverflow();
  if (!r) return null;
  const who = r.worst.map(w => w.what + ' → ' + w.right).join('  ·  ');
  return `schermo ${r.screen} · pagina ${r.page} (+${r.over})  ·  ${who || 'nessun elemento identificato'}`;
}

export default findOverflow;
